// The desktop app (desktop/, Electron): its preload puts `window.bbDesktop` on every
// page it opens. This file is the only place the web app talks to it; on the web every
// call is a no-op, so pages can use it without asking first.

const bridge = () => (typeof window !== 'undefined' ? window.bbDesktop : undefined);
const noop = () => {};
const off = () => noop;

export const isDesktop = () => !!bridge();

/** 'linux' | 'win32' | 'darwin' in the desktop app, else null. */
export const desktopPlatform = () => bridge()?.platform || null;

const call = (name, ...args) => {
  const fn = bridge()?.[name];
  return typeof fn === 'function' ? fn(...args) : undefined;
};
const listen = (name, cb) => {
  const fn = bridge()?.[name];
  return typeof fn === 'function' ? fn(cb) || noop : off();
};

export const desktop = {
  info: () => bridge()?.info || {},
  // Windows
  openMain: (path = '/app') => call('openMain', path),
  openQuick: (mode) => call('openQuick', mode),
  hideQuick: () => call('hideQuick'),
  pinQuick: (pinned) => call('pinQuick', pinned),
  resizeQuick: (width, height) => call('resizeQuick', width, height),
  onQuickOpen: (cb) => listen('onQuickOpen', cb),
  setHiveExpanded: (width) => call('setHiveExpanded', width),
  // The hive takes the mouse only over the drop and its cards (desktop/src/main.js hive:interactive,
  // hive:shape on Linux); elsewhere clicks reach the window under it.
  setHiveInteractive: (on) => call('setHiveInteractive', on),
  setHiveShape: (rects) => call('setHiveShape', rects),
  dragHive: (phase, x, y, fromEdge) => call('dragHive', phase, x, y, fromEdge),
  openBotView: (projectId, botId) => call('openBotView', projectId, botId),
  openExternal: (url) => call('openExternal', url),
  // A service that lets only desktop apps sign in (desktop/src/loopback.js): { port }
  mcpLoopback: (texts) => call('mcpLoopback', texts),
  onMcpLoopback: (cb) => listen('onMcpLoopback', cb),
  canSignInForApps: () => typeof bridge()?.mcpLoopback === 'function',
  // Sign-in through the system browser (routes/desktop.py)
  signInWithBrowser: () => call('signInWithBrowser'),
  signOut: () => call('signOut'),
  // System notifications with buttons
  notify: (spec) => call('notify', spec),
  closeNotifications: (tags) => call('closeNotifications', tags),
  onNotificationAction: (cb) => listen('onNotificationAction', cb),
  // The computer's idle time in seconds ({ idle, state }), or undefined before app 0.2.4
  systemIdle: () => call('systemIdle'),
  // Focus sessions (the main process keeps the timer)
  startFocus: (minutes, label, taskId) => call('startFocus', minutes, label, taskId),
  stopFocus: () => call('stopFocus'),
  onFocus: (cb) => listen('onFocus', cb),
  // What the tray, the dock icon and the GNOME extension show
  publishState: (state) => call('publishState', state),
  setStrings: (strings) => call('setStrings', strings),
  // Files and screenshots handed to the app (file manager, drag and drop, portal)
  takeScreenshot: () => call('takeScreenshot'),
  onFiles: (cb) => listen('onFiles', cb),
  readFile: (path) => call('readFile', path),
  // Settings of the app itself
  getSettings: () => call('getSettings'),
  setSetting: (key, value) => call('setSetting', key, value),
  onSettings: (cb) => listen('onSettings', cb),
  // Morning and evening cards, asked by the main process
  onRitual: (cb) => listen('onRitual', cb),
};
